import clsx from "clsx"
import { forwardRef, type HTMLAttributes, type InputHTMLAttributes, type ReactNode, useEffect, useRef, useState } from "react"
import { Controller, type FieldValues } from "react-hook-form"
import type { CompoundedComponent, IFormControl } from "@/shared/types"
import "./style.scss"
import { useTranslation } from "react-i18next"
import { cn } from "@/shared/lib/utils.ts"

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: string
  customError?: ReactNode | null
  label?: string
  startIcon?: ReactNode
  endIcon?: ReactNode
  debounceDelay?: number
  required?: boolean
  labelProps?: HTMLAttributes<HTMLLabelElement>
  suffix?: string
  prefix?: string
  suffixClassName?: string
  onlyNumbers?: boolean
  maskType?: MaskType
}

type MaskType = "phone" | "passport" | "none" | "jshshir" | "tin_individual" | "tin_legal"
const applyMask = (value: string, type: MaskType): string => {
  const clean = value.replace(/\D/g, "")

  switch (type) {
    case "phone": {
      const numbers = clean.replace(/^998/, "").slice(0, 9)
      const parts = [numbers.slice(0, 2), numbers.slice(2, 5), numbers.slice(5, 7), numbers.slice(7, 9)].filter(Boolean)
      return `+998 ${parts.map((p, i) => (i === 0 ? p : i === 1 ? ` ${p}` : `-${p}`)).join("")}`
    }

    case "passport": {
      const val = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase()
      if (!val) return ""
      const letters = val.slice(0, 2).replace(/[^A-Z]/g, "")
      const digits = val.slice(2).replace(/\D/g, "")

      return `${letters}${digits ? " " + digits : ""}`
    }

    case "tin_individual": {
      return clean.slice(0, 9)
    }

    case "tin_legal": {
      return clean.slice(0, 14)
    }

    case "jshshir": {
      return clean.slice(0, 14)
    }

    default:
      return value
  }
}
const extractRawValue = (masked: string, type: MaskType): string => {
  switch (type) {
    case "phone":
      return masked.replace(/\D/g, "").replace(/^998/, "")
    case "passport":
      return masked.replace(/\s/g, "")
    case "tin_individual":
    case "tin_legal":
    case "jshshir":
      return masked.replace(/\D/g, "")
    default:
      return masked
  }
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ maskType, error, label, labelProps, startIcon, customError, endIcon, debounceDelay, required, ...props }, ref) => {
    const debounceTimer = useRef<NodeJS.Timeout | null>(null)
    const [inputValue, setInputValue] = useState()
    const { t } = useTranslation("errors")
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawInput = e.target.value
      const masked = maskType ? applyMask(rawInput, maskType) : rawInput
      const unmasked = maskType ? extractRawValue(masked, maskType) : rawInput

      setInputValue(masked as any)

      const event = {
        ...e,
        target: {
          ...e.target,
          value: unmasked
        }
      }
      if (!debounceDelay || debounceDelay <= 0) {
        props.onChange?.(event as React.ChangeEvent<HTMLInputElement>)
      } else {
        if (debounceTimer.current) clearTimeout(debounceTimer.current)
        debounceTimer.current = setTimeout(() => {
          props.onChange?.(event as React.ChangeEvent<HTMLInputElement>)
        }, debounceDelay)
      }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!props.onlyNumbers) return

      const allowedKeys = ["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"]

      if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) {
        return
      }

      if (allowedKeys.includes(e.key) || (e.key >= "0" && e.key <= "9")) {
        return
      }

      e.preventDefault()
    }

    useEffect(() => {
      if (maskType) {
        const masked = applyMask((props.value as string) ?? "", maskType ?? "none")
        setInputValue(masked as any)
      } else {
        setInputValue(props.value as any)
      }
    }, [props.value, maskType])

    return (
      <div className={clsx("input relative", error != null && "input-error")}>
        <span className="input-label">
          {label}
          {(error || required) && <strong className="ml-0.5 text-[10px] text-red-500">＊</strong>}
        </span>
        <label {...labelProps} className={cn(`relative overflow-hidden bg-paper p-0 ${label ? "mt-2" : "mt-0"}`, labelProps?.className)}>
          {startIcon ? <div className="input-start-icon round">{startIcon}</div> : null}
          {props.prefix && (
            <span className="absolute left-0 flex h-full items-center justify-center bg-ui-background-soft px-2 text-gray-500 text-sm">
              {props.prefix}
            </span>
          )}

          <input onKeyDown={handleKeyDown} {...props} onChange={handleChange} ref={ref} value={inputValue} />

          {props.suffix && (
            <span
              className={cn(
                "absolute right-0 flex h-full items-center justify-center bg-ui-background-soft px-2 text-gray-500 text-sm",
                props.suffixClassName
              )}
            >
              {props.suffix}
            </span>
          )}

          {endIcon ? <div className="input-end-icon">{endIcon}</div> : null}
        </label>
        {error && <span>{t(error)}</span>}
        {customError}
      </div>
    )
  }
) as CompoundedComponent<InputProps, HTMLInputElement, { Form: typeof FormInput }>
const FormInput = <T extends FieldValues>({ control, name, rules, required = false, ...props }: IFormControl<T> & InputProps) => {
  return (
    <Controller
      control={control}
      name={name}
      rules={{
        ...(required ? { required: { value: true, message: "Required field" } } : {}),
        ...rules
      }}
      render={({ field, fieldState }) => (
        <Input
          {...field}
          {...props}
          required={required}
          error={fieldState.error?.message}
          value={field.value ?? undefined}
          onChange={e => {
            field.onChange(e)
            props.onChange?.(e)
          }}
        />
      )}
    />
  )
}

Input.Form = FormInput
